<template>
  <v-card :loading="loading" v-bind="$attrs" v-on="$listeners">
    <v-card-title>
      <v-row align="center">
        <v-col>
          <span class="headline">Dealer Salesperson Pending Payouts</span>
        </v-col>
        <v-col cols="auto">
          <v-btn
            icon
            title="Export CSV"
            aria-label="Export CSV"
            :disabled="loading || isExporting || !items.length"
            @click="exportDisplayedData"
          >
            <v-icon v-if="!isExporting">mdi-download</v-icon>
            <v-progress-circular v-else indeterminate color="primary" size="20" width="2" />
          </v-btn>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-subtitle>
      <span v-if="selectedFiscalYear && selectedFiscalYear.fiscalYearStart && selectedFiscalYear.fiscalYearEnd">
        <b>
          ({{ selectedFiscalYear.fiscalYearStart | formatDateClient("MM/DD/YYYY", selectedClient) }} -
          {{ selectedFiscalYear.fiscalYearEnd | formatDateClient("MM/DD/YYYY", selectedClient) }})
        </b>
        <br />
      </span>
      Pending payouts by salesperson.
      <br />
      Currently displaying only salespersons with payouts.
    </v-card-subtitle>

    <v-card-text>
      <v-alert v-if="exportError" type="error" text>{{ exportError }}</v-alert>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        dense
        :options.sync="options"
        :server-items-length="total"
      >
        <template v-slot:item.participantFullName="{ item }">
          <a @click.prevent="viewSalespersonDetails(item.participantId)" href="#">
            {{ item.participantFirstName }} {{ item.participantLastName }}
          </a>
        </template>
        <template v-slot:item.missingRequiredInformation="{ item }">
          <v-icon v-if="!item.missingRequiredInformation" color="green">mdi-check</v-icon>
          <v-icon v-else color="red">mdi-close</v-icon>
        </template>
        <template v-slot:item.pendingAmount="{ item }">
          {{ (item.pendingAmount || 0) | toCurrency }}
        </template>
        <template v-slot:item.organizationName="{ item }">
          {{ item.organizationName }} ({{ item.organizationKey }})
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "DealerSalespersonsPayouts",
  data() {
    return {
      loading: false,
      isExporting: false,
      exportError: "",
      items: [],
      total: 0,
      options: {
        itemsPerPage: 10,
        page: 1,
        sortDesc: [true]
      },
      headers: [
        { text: "Salesperson Name", value: "participantFullName" },
        { text: "Dealer Name", value: "organizationName" },
        { text: "Pending", value: "pendingAmount" },
        {
          text: this.$i18n.translate("Profile Complete?"),
          value: "missingRequiredInformation",
          sortable: false,
          align: "center"
        }
      ]
    };
  },
  computed: {
    ...mapGetters(["selectedParticipant", "selectedClient", "selectedFiscalYear"])
  },
  watch: {
    selectedFiscalYear() {
      this.fetchData();
    },
    options: {
      handler() {
        this.fetchData();
      },
      deep: true
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    getPayoutFilters() {
      const filters = {
        participantParentOrganization: { id: this.selectedParticipant.organization?.id },
        payoutStatus: "pending"
      };

      if (this.selectedFiscalYear?.fiscalYearStart) {
        filters.createdAfter = this.selectedFiscalYear.fiscalYearStart;
      }
      if (this.selectedFiscalYear?.fiscalYearEnd) {
        filters.createdBefore = this.$util
          .getDateInClientTimezone(this.selectedFiscalYear.fiscalYearEnd)
          .add(1, "day")
          .toDate();
      }

      return filters;
    },
    fetchData() {
      this.loading = true;
      const filters = this.getPayoutFilters();
      const params = this.$util.buildPaginationParams(this.options);

      this.$api
        .post(`/api/payouts/search/group/byParticipant?${params.toString()}`, filters)
        .then(({ data }) => {
          this.items = data.content;
          this.total = data.totalElements;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    viewSalespersonDetails(salespersonId) {
      this.$router.push({ name: "salespersonDetails", query: { salespersonId } });
    },
    async exportDisplayedData() {
      this.isExporting = true;
      this.exportError = "";
      try {
        const salespersons = await this.$util.fetchPaginatedData(
          "/api/payouts/search/group/byParticipant",
          this.getPayoutFilters()
        );
        const columns = this.headers.map(header => {
          if (header.value === "participantFullName") {
            return {
              ...header,
              format: row => [row.participantFirstName, row.participantLastName].filter(Boolean).join(" ")
            };
          }
          if (header.value === "organizationName") {
            return { ...header, format: row => `${row.organizationName || ""} (${row.organizationKey || ""})` };
          }
          if (header.value === "pendingAmount") {
            return { ...header, format: row => this.$util.toCurrency(row.pendingAmount || 0) };
          }
          if (header.value === "missingRequiredInformation") {
            return { ...header, format: row => !row.missingRequiredInformation };
          }
          return header;
        });
        this.$export.exportToCsv("dealer_salesperson_pending_payouts", columns, salespersons);
      } catch (error) {
        this.exportError =
          this.$kawasaki.getApiErrorMessages(error?.response?.data)[0] ||
          error?.message ||
          "Unable to export the report. Please try again.";
      } finally {
        this.isExporting = false;
      }
    }
  }
};
</script>
